import React, { useEffect, useMemo, useState } from 'react';
import useAnimeReveal from '../hooks/useAnimeReveal';
import useAnimePageLoad from '../hooks/useAnimePageLoad';
import teamHero from '../assets/AppImages/home_page/know-how.jpg';
import ceoImage from '../assets/AppImages/home_page/ceo2-primary-cutout.png';
import ashleyChokodzoImage from '../assets/AppImages/Team Images/Ashley Chokodzo cutout.png';
import floraMcleodImage from '../assets/AppImages/Team Images/Flora McLeod cutout.png';
import lawsonMpameImage from '../assets/AppImages/Team Images/Lawson Mpame cutout.png';
import makomboreroMatopeImage from '../assets/AppImages/Team Images/Makomborero Matope cutout.png';
import princessMbokadziImage from '../assets/AppImages/Team Images/Princess Mbokadzi cutout.png';
import tariroZendaImage from '../assets/AppImages/Team Images/Tariro Zenda cutout.png';

const teamMembers = [
  {
    name: 'Brian Langsoni',
    role: 'Chief Executive Officer',
    group: 'Core Team',
    bio: "Visionary leader and founder of DeRoyalty Car Rental, driving the company's mission to deliver world-class luxury car rental services across Zimbabwe and beyond.",
    focus: 'Strategy',
    image: ceoImage,
    imageClassName: 'deroyalty-team-photo-ceo',
  },
  /*
  Temporarily hidden per client request; keep details here so the role can be restored later.
  {
    name: 'Annitjie Palandwa',
    role: 'Operations Manager',
    group: 'Core Team',
    bio: 'Oversees day-to-day operations across all branches, ensuring seamless service delivery and the highest standards of customer satisfaction.',
    focus: 'Operations',
  },
  */
  {
    name: 'Cecilia Mashingaidze',
    role: 'HR Manager',
    group: 'Core Team',
    bio: 'Leads our human resources strategy, nurturing talent and cultivating a team culture rooted in excellence, integrity and genuine hospitality.',
    focus: 'People',
  },
  {
    name: 'Trymore Manyande',
    role: 'Fleet Manager',
    group: 'Core Team',
    bio: 'Manages and maintains the DeRoyalty fleet to the highest mechanical and aesthetic standards, ensuring every vehicle is road-ready and immaculate.',
    focus: 'Fleet',
  },
  {
    name: 'Tariro Zenda',
    role: 'Administrator',
    group: 'Core Team',
    bio: 'Keeps the administrative backbone of DeRoyalty running smoothly, from compliance and documentation to internal communications and coordination.',
    focus: 'Admin',
    image: tariroZendaImage,
    imageClassName: 'deroyalty-team-photo-tariro',
  },
  {
    name: 'Ashley Chokodzo',
    role: 'Sales Marketer',
    group: 'Core Team',
    bio: 'Champions the DeRoyalty brand across all platforms, crafting campaigns and building client relationships that reflect the luxury and reliability we stand for.',
    focus: 'Growth',
    image: ashleyChokodzoImage,
    imageClassName: 'deroyalty-team-photo-headshot',
  },
  /*
  Temporarily hidden per client request; keep details here so the team member can be restored later.
  {
    name: 'Onwell Zvarevashe',
    role: 'IT & Digital Services',
    group: 'Core Team',
    bio: 'Drives digital innovation and technology infrastructure, ensuring a seamless online experience and dependable digital solutions for every DeRoyalty client.',
    focus: 'Digital',
  },
  */
  {
    name: 'Lawson Mpame',
    role: 'Brand Ambassador - Ireland',
    group: 'Brand Ambassadors',
    bio: 'Represents the DeRoyalty brand with distinction across Ireland, extending our reputation for luxury and reliability to the European market.',
    focus: 'Europe',
    image: lawsonMpameImage,
  },
  {
    name: 'Princess Mbokadzi',
    role: 'Brand Ambassador - South Africa',
    group: 'Brand Ambassadors',
    bio: "Represents DeRoyalty across South Africa, building brand presence and connecting clients with Zimbabwe's premier car rental experience.",
    focus: 'South Africa',
    image: princessMbokadziImage,
  },
  {
    name: 'Flora McLeod',
    role: 'Brand Ambassador - Botswana',
    group: 'Brand Ambassadors',
    bio: 'Extends the DeRoyalty footprint into Botswana, showcasing the royal standard of service that defines everything we do.',
    focus: 'Botswana',
    image: floraMcleodImage,
  },
  {
    name: 'Makomborero Matope',
    role: 'Brand Ambassador - Zimbabwe',
    group: 'Brand Ambassadors',
    bio: "Represents DeRoyalty on home ground, connecting with local clients and embodying the brand's vision of luxury, reliability and excellence.",
    focus: 'Zimbabwe',
    image: makomboreroMatopeImage,
  },
  {
    name: 'Talent Hachundu',
    role: 'Brand Ambassador - Zambia',
    group: 'Brand Ambassadors',
    bio: 'Carries the DeRoyalty brand into Zambia, building relationships and extending our premium car rental services across Southern Africa.',
    focus: 'Zambia',
  },
];

const playbookItems = [
  {
    icon: 'bi-headset',
    title: 'Client-first reservations',
    text: 'Every enquiry is handled with clarity, speed and care so clients know exactly what happens next.',
  },
  {
    icon: 'bi-car-front',
    title: 'Fleet care',
    text: 'Vehicle readiness, cleanliness and condition checks sit at the centre of the DeRoyalty experience.',
  },
  {
    icon: 'bi-shield-check',
    title: 'Service accountability',
    text: 'Managers, administrators and ambassadors work together to keep every promise visible and traceable.',
  },
  {
    icon: 'bi-lightning-charge',
    title: 'Digital confidence',
    text: 'Our IT and digital services support smoother booking, clearer communication and better client follow-up.',
  },
];

function getInitials(name) {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function MeetTheTeam() {
  const [activeMember, setActiveMember] = useState(0);
  const spotlightMembers = teamMembers
    .filter((member) => member.group === 'Core Team')
    .slice(0, 6);

  const rootRef = useAnimePageLoad({ selector: '[data-anime-enter]' });
  useAnimeReveal({ selector: '[data-anime]', rootRef });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveMember((current) => (current + 1) % spotlightMembers.length);
    }, 6200);

    return () => window.clearInterval(timer);
  }, [spotlightMembers.length]);

  const groups = useMemo(() => {
    return teamMembers.reduce((accumulator, member) => {
      if (!accumulator[member.group]) {
        accumulator[member.group] = [];
      }
      accumulator[member.group].push(member);
      return accumulator;
    }, {});
  }, []);

  const showPreviousMember = () => {
    setActiveMember((current) => (current - 1 + spotlightMembers.length) % spotlightMembers.length);
  };

  const showNextMember = () => {
    setActiveMember((current) => (current + 1) % spotlightMembers.length);
  };

  return (
    <main className="deroyalty-team-page" ref={rootRef}>
      <section className="deroyalty-team-hero" aria-label="Meet the DeRoyalty team">
        <img src={teamHero} alt="" aria-hidden="true" />
        <div className="deroyalty-team-hero-copy" data-anime-enter>
          <p className="deroyalty-eyebrow">People behind the royal journey</p>
          <h1>Meet the Team</h1>
          <p>
            Disciplined operators, fleet specialists and brand ambassadors keep every DeRoyalty
            journey clear, polished and supported from reservation to return.
          </p>
          <div className="deroyalty-team-hero-stats" aria-label="Team highlights">
            <span><strong>{teamMembers.length}</strong> Team members</span>
            <span><strong>6</strong> Markets supported</span>
            <span><strong>24/7</strong> Client care</span>
          </div>
        </div>
      </section>

      <section className="deroyalty-team-section deroyalty-team-intro deroyalty-team-command-grid" data-anime aria-labelledby="team-intro-title">
        <div className="deroyalty-team-card">
          <p className="deroyalty-eyebrow">How we work</p>
          <h2 id="team-intro-title">A service team built like a premium operating system.</h2>
          <p>
            We believe world-class service begins with world-class people: disciplined operators, attentive hosts,
            careful fleet managers, trusted ambassadors and digital specialists who keep the client journey simple.
          </p>
        </div>

        <div className="deroyalty-team-playbook deroyalty-team-tools-grid" data-anime-stagger="true" aria-label="DeRoyalty team playbook">
          {playbookItems.map((item, index) => (
            <article className="deroyalty-team-playbook-card deroyalty-team-tool-card" key={item.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <i className={`bi ${item.icon}`} aria-hidden="true" />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="deroyalty-team-section deroyalty-team-spotlight" data-anime aria-labelledby="team-spotlight-title">
        <div className="deroyalty-team-section-head">
          <p className="deroyalty-eyebrow">Leadership and operations</p>
          <h2 id="team-spotlight-title">Team spotlight</h2>
        </div>

        <div className="deroyalty-team-spotlight-shell">
          <div
            className="deroyalty-team-spotlight-track"
            style={{ transform: `translateX(-${activeMember * 100}%)` }}
          >
            {spotlightMembers.map((member, index) => (
              <article
                className="deroyalty-team-spotlight-slide"
                key={member.name}
                aria-hidden={index !== activeMember}
              >
                <div className={`deroyalty-team-profile-art ${member.image ? '' : 'has-fallback'} ${member.imageClassName || ''}`} aria-hidden="true">
                  {member.image ? (
                    <img src={member.image} alt="" />
                  ) : (
                    <span>{getInitials(member.name)}</span>
                  )}
                </div>
                <div className="deroyalty-team-spotlight-copy">
                  <p className="deroyalty-eyebrow">{member.focus}</p>
                  <h3>{member.name}</h3>
                  <strong>{member.role}</strong>
                  <p>{member.bio}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="deroyalty-team-slider-controls">
            <button type="button" onClick={showPreviousMember} aria-label="Previous team member">
              <i className="bi bi-arrow-left" aria-hidden="true" />
            </button>
            <div className="deroyalty-team-slider-dots" aria-hidden="true">
              {spotlightMembers.map((member, index) => (
                <span key={member.name} className={index === activeMember ? 'active' : ''} />
              ))}
            </div>
            <button type="button" onClick={showNextMember} aria-label="Next team member">
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      {Object.entries(groups).map(([groupName, members]) => (
        <section className="deroyalty-team-section deroyalty-team-roster" data-anime key={groupName}>
          <div className="deroyalty-team-section-head">
            <p className="deroyalty-eyebrow">People directory</p>
            <h2>{groupName}</h2>
          </div>
          <div className="deroyalty-team-grid" data-anime-stagger="true">
            {members.map((member) => (
              <article className="deroyalty-team-person-card" key={member.name}>
                <div className={`deroyalty-team-avatar ${member.image ? 'has-photo' : 'has-fallback'} ${member.imageClassName || ''}`} aria-hidden="true">
                  {member.image ? <img src={member.image} alt="" /> : <span>{getInitials(member.name)}</span>}
                </div>
                <div>
                  <h3>{member.name}</h3>
                  <strong>{member.role}</strong>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}

      <section className="deroyalty-team-cta" data-anime data-anime-direction="scale" aria-labelledby="team-cta-title">
        <p className="deroyalty-eyebrow">The royal journey continues</p>
        <h2 id="team-cta-title">Every great rental experience begins with people who care.</h2>
        <a href="/contact" className="deroyalty-team-action">
          Talk to the Team
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </a>
      </section>
    </main>
  );
}

export default MeetTheTeam;
